/**
 * CONTROLE DE TRAFO EM REFORMA — o lado da tela: chamadas à API e pequenos
 * formatadores. As regras do fluxo (etapas, listas, o que falta para concluir)
 * estão em ./trafos-regras.ts, que o servidor também usa.
 *
 * ARQUIVO IGUAL no sistemaColaborador e no sistemaOperacional.
 */
import { ApiError, usuarioDaSessao } from "./api";
import type {
  ColetaOleo,
  Ensaio,
  EtapaOuConcluido,
  ItemInspecao,
  Servico,
  StatusServico,
} from "./trafos-regras";

export * from "./trafos-regras";

/* ---------------------------------------------------------------- tipos --- */

/** Linha da lista (leve: sem ensaios, sem fotos). */
export interface TrafoResumo {
  id: number;
  etapa: EtapaOuConcluido;
  etapa_desde: string;
  cliente: string;
  os_numero: string | null;
  nota_fiscal: string | null;
  identificacao: string | null;
  data_entrada: string;
  previsao_entrega: string | null;
  parecer_final: string | null;
  data_conclusao: string | null;
  criado_por: string | null;
  atualizado_por: string | null;
  updated_at: string;
  fabricante: string | null;
  potencia_kva: string | null;
  numero_serie: string | null;
  servicos_total: number;
  servicos_feitos: number;
  servicos_andamento: number;
  ultimo_texto: string | null;
  ultimo_usuario: string | null;
  ultimo_em: string | null;
}

export interface EventoTrafo {
  id: number;
  etapa: string | null;
  tipo: string;
  texto: string;
  usuario: string | null;
  created_at: string;
}

/** Movimentação no painel da gestão (evento + de qual trafo). */
export interface Movimento extends EventoTrafo {
  trafo_id: number;
  cliente: string;
  os_numero: string | null;
  fabricante: string | null;
  potencia_kva: string | null;
  etapa_atual: EtapaOuConcluido;
}

export interface FotoTrafoInfo {
  id: number;
  etapa: string;
  legenda: string | null;
  usuario: string | null;
  created_at: string;
  pode_apagar: boolean;
}

export interface FotoTrafo {
  id: number;
  etapa: string;
  legenda: string | null;
  url: string;
}

/** A ficha completa do trafo. */
export interface Trafo {
  id: number;
  etapa: EtapaOuConcluido;
  etapa_desde: string;
  cliente: string;
  os_numero: string | null;
  nota_fiscal: string | null;
  identificacao: string | null;
  data_entrada: string;
  previsao_entrega: string | null;
  condicao_chegada: string | null;
  placa: Record<string, unknown>;
  servicos: Servico[];
  ensaios_entrada: Ensaio[];
  coleta_oleo: ColetaOleo;
  ensaios_finais: Ensaio[];
  inspecao_final: ItemInspecao[];
  parecer_final: string | null;
  parecer_obs: string | null;
  responsavel_qualidade: string | null;
  data_conclusao: string | null;
  criado_por: string | null;
  atualizado_por: string | null;
  created_at: string;
  updated_at: string;
  eventos: EventoTrafo[];
  fotos: FotoTrafoInfo[];
  pode_excluir: boolean;
}

/** Tudo o que o relatório de rastreabilidade imprime (ver src/trafo-folha.ts). */
export interface RastreabilidadeTrafo extends Omit<Trafo, "fotos" | "pode_excluir"> {
  /** O histórico INTEIRO, do mais antigo para o mais novo. */
  eventos: EventoTrafo[];
  fotos: (FotoTrafo & { usuario: string | null; created_at: string })[];
  emitido_por: string;
  emitido_em: string;
}

export interface EntradaTrafo {
  cliente: string;
  os_numero: string;
  nota_fiscal: string;
  identificacao: string;
  data_entrada: string;
  previsao_entrega: string;
  condicao_chegada: string;
}

/* ------------------------------------------------------------------ API --- */

async function chamar<T>(caminho: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`/api/trafos${caminho}`, {
    ...init,
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      "x-sercamp-usuario": usuarioDaSessao(),
      ...(init.headers ?? {}),
    },
  });
  if (!res.ok) {
    let msg = `Erro ${res.status}`;
    let code = "ERROR";
    try {
      const body = await res.json();
      if (body?.error) msg = body.error;
      if (body?.code) code = body.code;
    } catch {
      /* resposta sem JSON */
    }
    throw new ApiError(msg, code);
  }
  return res.json() as Promise<T>;
}

const post = <T>(caminho: string, corpo: unknown) =>
  chamar<T>(caminho, { method: "POST", body: JSON.stringify(corpo) });

export const apiTrafos = {
  listar: (situacao: "andamento" | "concluidos" | "todos" = "todos") =>
    chamar<TrafoResumo[]>(`?situacao=${situacao}`),
  movimentos: (limite = 40) => chamar<Movimento[]>(`/movimentos?limite=${limite}`),
  detalhe: (id: number) => chamar<Trafo>(`/${id}`),
  rastreabilidade: (id: number, fotos = true) =>
    chamar<RastreabilidadeTrafo>(`/${id}/rastreabilidade${fotos ? "" : "?fotos=0"}`),
  fotos: (id: number, etapa: string) => chamar<FotoTrafo[]>(`/${id}/fotos?etapa=${encodeURIComponent(etapa)}`),
  criar: (dados: EntradaTrafo) => post<{ id: number }>("", dados),
  salvar: (id: number, etapa: string, dados: Record<string, unknown>) =>
    chamar<{ ok: boolean }>(`/${id}`, { method: "PUT", body: JSON.stringify({ etapa, dados }) }),
  statusServico: (id: number, servicoId: string, status: StatusServico, obs?: string) =>
    post<{ ok: boolean; servicos: Servico[] }>(`/${id}/servicos`, { acao: "status", servico_id: servicoId, status, obs }),
  /** Só a observação do serviço (não mexe no status, que outro pode ter mudado). */
  obsServico: (id: number, servicoId: string, obs: string) =>
    post<{ ok: boolean; servicos: Servico[] }>(`/${id}/servicos`, { acao: "obs", servico_id: servicoId, obs }),
  adicionarServico: (id: number, nome: string) =>
    post<{ ok: boolean; servicos: Servico[] }>(`/${id}/servicos`, { acao: "adicionar", nome }),
  avancar: (id: number, etapa: string) => post<{ ok: boolean; etapa: EtapaOuConcluido }>(`/${id}/avancar`, { etapa }),
  /** `de` = a etapa que a pessoa estava vendo (o servidor recusa se o trafo já mudou). */
  devolver: (id: number, para: string, motivo: string, de: string) =>
    post<{ ok: boolean; etapa: EtapaOuConcluido }>(`/${id}/devolver`, { para, motivo, de }),
  registro: (id: number, etapa: string, texto: string, fotos: string[]) =>
    post<{ ok: boolean }>(`/${id}/registro`, { etapa, texto, fotos }),
  enviarFotos: (id: number, etapa: string, fotos: string[]) =>
    post<{ ok: boolean }>(`/${id}/fotos`, { etapa, fotos }),
  apagarFoto: (id: number, fotoId: number) =>
    chamar<{ ok: boolean }>(`/${id}/fotos/${fotoId}`, { method: "DELETE" }),
  excluir: (id: number) => chamar<{ ok: boolean }>(`/${id}`, { method: "DELETE" }),
};

/* ---------------------------------------------------------- formatação --- */

/** AAAA-MM-DD → DD/MM/AAAA (sem passar por Date, para não escorregar de fuso). */
export function dataBR(iso: string | null | undefined): string {
  if (!iso) return "—";
  const [a, m, d] = String(iso).slice(0, 10).split("-");
  return a && m && d ? `${d}/${m}/${a}` : String(iso);
}

/** Data e hora local ("13/10 14:32"). */
export function dataHora(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

/** "agora", "há 5 min", "há 3 h", "há 2 dias". */
export function haQuanto(iso: string | null | undefined, agora = Date.now()): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const min = Math.max(0, Math.floor((agora - t) / 60_000));
  if (min < 1) return "agora";
  if (min < 60) return `há ${min} min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `há ${h} h`;
  const dias = Math.floor(h / 24);
  return `há ${dias} dia${dias > 1 ? "s" : ""}`;
}

/** Tempo parado na etapa, curto ("2 h", "3 dias"). */
export function duracao(iso: string | null | undefined, agora = Date.now()): string {
  const txt = haQuanto(iso, agora);
  return txt === "agora" ? "agora" : txt.replace(/^há /, "");
}

/** Hoje em AAAA-MM-DD (horário local). */
export function hojeYmd(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** Entrega prevista já passou (e o trafo não foi concluído)? */
export function entregaAtrasada(t: { previsao_entrega: string | null; etapa: string }): boolean {
  return !!t.previsao_entrega && t.etapa !== "concluido" && t.previsao_entrega < hojeYmd();
}

/** Como o trafo é chamado na oficina: "WEG · 500 kVA · série 1234". */
export function descricaoTrafo(t: {
  fabricante?: string | null;
  potencia_kva?: string | null;
  numero_serie?: string | null;
  identificacao?: string | null;
}): string {
  const partes = [
    t.fabricante,
    t.potencia_kva ? `${t.potencia_kva} kVA` : null,
    t.numero_serie ? `série ${t.numero_serie}` : null,
    t.identificacao,
  ].filter(Boolean);
  return partes.length > 0 ? partes.join(" · ") : "Placa ainda não coletada";
}
